import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import type { RootState } from '../../store';
import type { NotificationsResponse, ServerNotification } from './types';

export const POLL_MS = 30_000;

/**
 * GET /notifications every 30 s while the tab is visible, and again on focus and when the pending-order count
 * (read from the store, not polled here) changes. A resolved event is simply absent from the next answer.
 */
export const useNotifications = (restaurantId: string | null, locationId: string | null): { items: ServerNotification[]; error: boolean } => {
    const [items, setItems] = useState<ServerNotification[]>([]);
    const [error, setError] = useState(false);
    const pending = useSelector((s: RootState) => s.pendingOrders.count);
    const issued = useRef(0);
    const applied = useRef(0);

    const load = useCallback(async () => {
        if (document.hidden) return;
        const id = (issued.current += 1);
        try {
            const { data } = await api.get<NotificationsResponse>('/notifications', {
                params: { ...(locationId ? { locationId } : {}), ...(restaurantId ? { restaurantId } : {}) },
            });
            if (id < applied.current) return;
            applied.current = id;
            setItems(data.items);
            setError(false);
        } catch {
            setError(true);
        }
    }, [restaurantId, locationId]);

    useEffect(() => {
        void load();
        const timer = window.setInterval(() => void load(), POLL_MS);
        const refetch = () => void load();
        document.addEventListener('visibilitychange', refetch);
        window.addEventListener('focus', refetch);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refetch);
            window.removeEventListener('focus', refetch);
        };
    }, [load]);

    // The sidebar badge already knows when orders arrive or are handled: refresh then, without a second pending poll.
    const first = useRef(true);
    useEffect(() => {
        if (first.current) {
            first.current = false;
            return;
        }
        void load();
    }, [pending, load]);

    return { items, error };
};
